import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, MenuItem, TextField, Autocomplete,
  IconButton, Tooltip, CircularProgress, Chip, Grid, Paper, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  Home, Analytics, TrendingUp, TrendingDown, TrendingFlat, Assessment, WarningAmber, Insights, Person, Groups,
  Download, Print, Leaderboard,
} from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';

const API = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

export const BRAND = '#00695c';
const GRADE_COLORS = { EE: '#2e7d32', ME: '#1565c0', AE: '#f57c00', BE: '#c62828' };
export const gradeColor = (g) => GRADE_COLORS[(g || '').slice(0, 2)] || '#9e9e9e';
const PALETTE = ['#1565c0', '#6a1b9a', '#e65100', '#2e7d32', '#00838f', '#c62828', '#f9a825', '#4527a0', '#00695c', '#ad1457'];

export const TrendChip = ({ v, suffix = ' pts' }) => {
  if (v === null || v === undefined) return <Chip size="small" label="—" variant="outlined" />;
  if (v > 0) return <Chip size="small" icon={<TrendingUp />} label={`+${v}${suffix}`} sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700 }} />;
  if (v < 0) return <Chip size="small" icon={<TrendingDown />} label={`${v}${suffix}`} sx={{ bgcolor: '#ffebee', color: '#c62828', fontWeight: 700 }} />;
  return <Chip size="small" icon={<TrendingFlat />} label={`0${suffix}`} sx={{ bgcolor: '#eceff1', color: '#546e7a', fontWeight: 700 }} />;
};

export function LineChart({ points, projection, height = 220, color = BRAND }) {
  const W = 640, H = height, padL = 34, padR = 16, padT = 16, padB = 34;
  const n = points.length + (projection ? 1 : 0);
  if (!points.length) return <Typography color="text.secondary" variant="body2">No data.</Typography>;
  const x = (i) => padL + (n <= 1 ? 0 : (i * (W - padL - padR)) / (n - 1));
  const y = (v) => padT + (H - padT - padB) * (1 - v / 100);
  const linePts = points.map((p, i) => `${x(i)},${y(p.value)}`).join(' ');
  const projIdx = points.length;
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: 420, display: 'block' }}>
        {[0, 25, 40, 50, 75, 100].map(g => (
          <g key={g}>
            <line x1={padL} y1={y(g)} x2={W - padR} y2={y(g)} stroke={g === 40 ? '#ef9a9a' : '#eceff1'} strokeDasharray={g === 40 ? '4 3' : ''} />
            <text x={4} y={y(g) + 3} fontSize="9" fill="#90a4ae">{g}</text>
          </g>
        ))}
        <polyline points={linePts} fill="none" stroke={color} strokeWidth="2.5" />
        {projection && (
          <>
            <line x1={x(points.length - 1)} y1={y(points[points.length - 1].value)} x2={x(projIdx)} y2={y(projection.value)} stroke="#8e24aa" strokeWidth="2" strokeDasharray="5 4" />
            <circle cx={x(projIdx)} cy={y(projection.value)} r="5" fill="#fff" stroke="#8e24aa" strokeWidth="2.5" />
            <text x={x(projIdx)} y={y(projection.value) - 9} fontSize="10" fill="#8e24aa" textAnchor="middle" fontWeight="700">{projection.value}%</text>
            <text x={x(projIdx)} y={H - 8} fontSize="9" fill="#8e24aa" textAnchor="middle">{projection.label}</text>
          </>
        )}
        {points.map((p, i) => (
          <g key={i}>
            <circle cx={x(i)} cy={y(p.value)} r="4" fill={color} />
            <text x={x(i)} y={y(p.value) - 8} fontSize="10" fill="#37474f" textAnchor="middle" fontWeight="700">{p.value}</text>
            <text x={x(i)} y={H - 8} fontSize="9" fill="#607d8b" textAnchor="middle">{p.label}</text>
          </g>
        ))}
      </svg>
    </Box>
  );
}

// Multi-series line chart (one line per stage). Points aligned to `labels`;
// null values leave a gap (a stage with no data that term). Legend is rendered
// by the caller. Series: [{ name, color, points:[number|null] }].
export function MultiLineChart({ labels, series, height = 260 }) {
  const W = 680, H = height, padL = 34, padR = 14, padT = 16, padB = 32;
  if (!labels?.length || !series?.length) return <Typography color="text.secondary" variant="body2">No data.</Typography>;
  const n = labels.length;
  const x = (i) => padL + (n <= 1 ? (W - padL - padR) / 2 : (i * (W - padL - padR)) / (n - 1));
  const y = (v) => padT + (H - padT - padB) * (1 - v / 100);
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: 460, display: 'block' }}>
        {[0, 25, 40, 50, 75, 100].map(g => (
          <g key={g}>
            <line x1={padL} y1={y(g)} x2={W - padR} y2={y(g)} stroke={g === 40 ? '#ef9a9a' : '#eceff1'} strokeDasharray={g === 40 ? '4 3' : ''} />
            <text x={4} y={y(g) + 3} fontSize="9" fill="#90a4ae">{g}</text>
          </g>
        ))}
        {labels.map((lb, i) => <text key={i} x={x(i)} y={H - 8} fontSize="10" fill="#607d8b" textAnchor="middle">{lb}</text>)}
        {series.map((s, si) => {
          const color = s.color || PALETTE[si % PALETTE.length];
          const segs = []; let cur = [];
          s.points.forEach((v, i) => {
            if (v == null) { if (cur.length) { segs.push(cur); cur = []; } }
            else cur.push(`${x(i)},${y(v)}`);
          });
          if (cur.length) segs.push(cur);
          return (
            <g key={si}>
              {segs.map((seg, k) => <polyline key={k} points={seg.join(' ')} fill="none" stroke={color} strokeWidth="2.5" />)}
              {s.points.map((v, i) => v == null ? null : <circle key={i} cx={x(i)} cy={y(v)} r="3.5" fill={color} />)}
            </g>
          );
        })}
      </svg>
    </Box>
  );
}

export function Sparkline({ values, color = BRAND }) {
  const W = 90, H = 26, pad = 3;
  if (!values.length) return <span style={{ color: '#bdbdbd' }}>—</span>;
  const x = (i) => pad + (values.length <= 1 ? 0 : (i * (W - 2 * pad)) / (values.length - 1));
  const y = (v) => pad + (H - 2 * pad) * (1 - v / 100);
  return (
    <svg width={W} height={H} style={{ verticalAlign: 'middle' }}>
      <polyline points={values.map((v, i) => `${x(i)},${y(v)}`).join(' ')} fill="none" stroke={color} strokeWidth="1.8" />
      {values.map((v, i) => <circle key={i} cx={x(i)} cy={y(v)} r="1.8" fill={color} />)}
    </svg>
  );
}

export function StatTile({ label, value, sub, color = '#37474f' }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', height: '100%' }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, color, lineHeight: 1.2 }}>{value}</Typography>
      {sub != null && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Paper>
  );
}

function RiskList({ risks }) {
  return (
    <Box display="flex" flexDirection="column" gap={0.75}>
      {risks.map((r, i) => {
        const c = r.level === 'high' ? { bg: '#ffebee', fg: '#c62828' } : r.level === 'medium' ? { bg: '#fff3e0', fg: '#e65100' } : { bg: '#e8f5e9', fg: '#2e7d32' };
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: c.bg, borderRadius: 1, px: 1.5, py: 0.75 }}>
            <Chip size="small" label={r.level.toUpperCase()} sx={{ bgcolor: c.fg, color: '#fff', fontWeight: 700, height: 20 }} />
            <Typography variant="body2" sx={{ color: c.fg }}>{r.text}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Reusable learner-analytics body (used by staff + parent) ───────────────────
export function LearnerAnalyticsBody({ data }) {
  if (!data) return null;
  if (!data.periods?.length) return <Alert severity="info">No assessment scores found for this learner in the selected range.</Alert>;
  const overallSeries = data.periods.map(p => ({ label: p.chartLabel || p.label, value: p.overallMean }));
  return (
    <>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={2}><StatTile label="Overall mean" value={`${data.overall.mean}%`} sub={data.overall.grade} color={gradeColor(data.overall.grade)} /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatTile label="Latest" value={data.overall.latest != null ? `${data.overall.latest}%` : '—'} sub={data.periods[data.periods.length - 1]?.label} /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatTile label="Best" value={data.overall.best != null ? `${data.overall.best}%` : '—'} color="#2e7d32" /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatTile label="Lowest" value={data.overall.worst != null ? `${data.overall.worst}%` : '—'} color="#c62828" /></Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', height: '100%' }}>
            <Typography variant="caption" color="text.secondary">Trend</Typography>
            <Box mt={0.5}><TrendChip v={data.overall.trend} /></Box>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4} md={2}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', height: '100%', borderColor: '#ce93d8', bgcolor: '#faf5ff' }}>
            <Typography variant="caption" color="text.secondary">Projected next</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#8e24aa', lineHeight: 1.2 }}>{data.projection ? `${data.projection.projectedMean}%` : '—'}</Typography>
            <Typography variant="caption" color="text.secondary">{data.projection ? data.projection.nextLabel : 'need 2+ periods'}</Typography>
          </Paper>
        </Grid>
      </Grid>

      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Overall mean over time {data.projection && <Typography component="span" variant="caption" sx={{ color: '#8e24aa', ml: 1 }}>— dashed = projection</Typography>}</Typography>
          <LineChart points={overallSeries} projection={data.projection ? { label: data.projection.nextLabel, value: data.projection.projectedMean } : null} />
        </CardContent>
      </Card>

      {data.byGrade?.length > 0 && (
        <Card elevation={2} sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Overall grade per stage
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>— fair like-for-like across grades (subjects change each grade)</Typography>
            </Typography>
            <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="stretch">
              {data.byGrade.map((g, i) => (
                <React.Fragment key={g.stageCode}>
                  <Paper variant="outlined" sx={{ p: 1.5, minWidth: 128, textAlign: 'center', borderColor: `${gradeColor(g.grade)}66`, bgcolor: `${gradeColor(g.grade)}0d` }}>
                    <Typography variant="caption" color="text.secondary">{g.stageCode}{g.years ? ` · ${g.years}` : ''}</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, color: gradeColor(g.grade), lineHeight: 1.2 }}>{g.mean}%</Typography>
                    <Chip size="small" label={g.grade || '—'} sx={{ bgcolor: gradeColor(g.grade), color: '#fff', fontWeight: 700, height: 20, mt: 0.5 }} />
                    <Box mt={0.5}><TrendChip v={g.trend} /></Box>
                    <Typography variant="caption" color="text.secondary" display="block">{g.termsCount} term(s)</Typography>
                  </Paper>
                  {i < data.byGrade.length - 1 && <Box display="flex" alignItems="center" sx={{ color: 'text.disabled' }}>→</Box>}
                </React.Fragment>
              ))}
            </Box>
          </CardContent>
        </Card>
      )}

      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" alignItems="center" gap={1} mb={1}><WarningAmber sx={{ color: '#f57c00' }} /><Typography variant="subtitle2" fontWeight={700}>Risk & opportunity flags</Typography></Box>
          <RiskList risks={data.risks} />
        </CardContent>
      </Card>

      <Card elevation={2}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Subject performance</Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 620 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>Subject</strong></TableCell>
                  <TableCell align="center"><strong>Trend</strong></TableCell>
                  <TableCell align="center"><strong>Mean</strong></TableCell>
                  <TableCell align="center"><strong>Latest</strong></TableCell>
                  <TableCell align="center"><strong>Best</strong></TableCell>
                  <TableCell align="center"><strong>Low</strong></TableCell>
                  <TableCell align="center"><strong>Δ</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.subjects.map((s, i) => (
                  <TableRow key={s.code} hover>
                    <TableCell>{s.name}</TableCell>
                    <TableCell align="center"><Sparkline values={s.points.map(p => p.pct)} color={PALETTE[i % PALETTE.length]} /></TableCell>
                    <TableCell align="center"><strong>{s.mean}%</strong></TableCell>
                    <TableCell align="center" sx={{ color: s.latest != null && s.latest < 40 ? '#c62828' : 'inherit', fontWeight: s.latest != null && s.latest < 40 ? 700 : 400 }}>{s.latest != null ? `${s.latest}%` : '—'}</TableCell>
                    <TableCell align="center" sx={{ color: '#2e7d32' }}>{s.best != null ? `${s.best}%` : '—'}</TableCell>
                    <TableCell align="center" sx={{ color: '#c62828' }}>{s.worst != null ? `${s.worst}%` : '—'}</TableCell>
                    <TableCell align="center"><TrendChip v={s.trend} suffix="" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </>
  );
}

// ── Class / cohort analytics body ──────────────────────────────────────────────
function ClassAnalyticsBody({ data }) {
  if (!data) return null;
  if (!data.periods?.length) return <Alert severity="info">No assessment scores found for this class in the selected range.</Alert>;
  const series = data.periods.map(p => ({ label: p.chartLabel || p.label, value: p.classMean }));
  const bands = ['EE', 'ME', 'AE', 'BE'];
  const distTotal = Object.values(data.distribution || {}).reduce((a, b) => a + b, 0) || 1;
  return (
    <>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={3} md={2}><StatTile label="Class mean" value={`${data.classMean}%`} /></Grid>
        <Grid item xs={6} sm={3} md={2}><StatTile label="Learners" value={data.scope.learnerCount} /></Grid>
        <Grid item xs={6} sm={3} md={2}><StatTile label="At risk" value={data.atRiskCount} color={data.atRiskCount ? '#c62828' : '#2e7d32'} /></Grid>
        <Grid item xs={6} sm={3} md={2}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', height: '100%', borderColor: '#ce93d8', bgcolor: '#faf5ff' }}>
            <Typography variant="caption" color="text.secondary">Projected next</Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#8e24aa', lineHeight: 1.2 }}>{data.projection ? `${data.projection.projectedMean}%` : '—'}</Typography>
            <Typography variant="caption" color="text.secondary">{data.projection ? data.projection.nextLabel : 'need 2+ periods'}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={12} md={4}>
          <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
            <Typography variant="caption" color="text.secondary">Grade distribution (all scores)</Typography>
            <Box sx={{ display: 'flex', height: 22, borderRadius: 1, overflow: 'hidden', mt: 0.75 }}>
              {bands.map(b => {
                const n = data.distribution?.[b] || 0;
                if (!n) return null;
                return <Box key={b} sx={{ width: `${(n / distTotal) * 100}%`, bgcolor: gradeColor(b), color: '#fff', fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{b} {n}</Box>;
              })}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Class mean over time {data.projection && <Typography component="span" variant="caption" sx={{ color: '#8e24aa', ml: 1 }}>— dashed = projection</Typography>}</Typography>
          <LineChart points={series} projection={data.projection ? { label: data.projection.nextLabel, value: data.projection.projectedMean } : null} />
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        {/* Per learner ranking */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Learner ranking</Typography>
              <TableContainer sx={{ maxHeight: 460, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>#</strong></TableCell>
                      <TableCell><strong>Learner</strong></TableCell>
                      <TableCell align="center"><strong>Trend</strong></TableCell>
                      <TableCell align="center"><strong>Mean</strong></TableCell>
                      <TableCell align="center"><strong>Latest</strong></TableCell>
                      <TableCell align="center"><strong>Δ</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.learners.map((l, i) => (
                      <TableRow key={l.admNo} hover>
                        <TableCell>{l.rank}</TableCell>
                        <TableCell>{l.name}</TableCell>
                        <TableCell align="center"><Sparkline values={l.points} color={PALETTE[i % PALETTE.length]} /></TableCell>
                        <TableCell align="center"><strong style={{ color: gradeColor(l.grade) }}>{l.mean}%</strong></TableCell>
                        <TableCell align="center" sx={{ color: l.latest != null && l.latest < 40 ? '#c62828' : 'inherit' }}>{l.latest != null ? `${l.latest}%` : '—'}</TableCell>
                        <TableCell align="center"><TrendChip v={l.trend} suffix="" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
        {/* Per learning area */}
        <Grid item xs={12} md={6}>
          <Card elevation={2} sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>Learning-area performance</Typography>
              <TableContainer sx={{ maxHeight: 460, overflow: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell><strong>Subject</strong></TableCell>
                      <TableCell align="center"><strong>Trend</strong></TableCell>
                      <TableCell align="center"><strong>Mean</strong></TableCell>
                      <TableCell align="center"><strong>Below 40</strong></TableCell>
                      <TableCell align="center"><strong>Δ</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.subjects.map((s, i) => (
                      <TableRow key={s.code} hover>
                        <TableCell>{s.name}</TableCell>
                        <TableCell align="center"><Sparkline values={s.points.map(p => p.pct)} color={PALETTE[i % PALETTE.length]} /></TableCell>
                        <TableCell align="center"><strong style={{ color: gradeColor(s.grade) }}>{s.mean}%</strong></TableCell>
                        <TableCell align="center" sx={{ color: s.belowCount ? '#c62828' : 'text.secondary' }}>{s.belowCount}/{s.entries}</TableCell>
                        <TableCell align="center"><TrendChip v={s.trend} suffix="" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </>
  );
}

// ── School-wide STAGE COMPARISON body ──────────────────────────────────────────
// Side-by-side cohort performance across every stage (grade) for one year.
function StageComparisonBody({ data }) {
  if (!data) return null;
  if (!data.stages?.length) return <Alert severity="info">No assessment scores found for {data.academicYear || 'the selected year'}.</Alert>;
  const ranked = [...data.stages].sort((a, b) => b.mean - a.mean);
  const best = ranked[0], weakest = ranked[ranked.length - 1];
  const series = data.stages.map((st, i) => ({ name: st.stageCode, color: PALETTE[i % PALETTE.length], points: st.points.map(p => p.value) }));
  return (
    <>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} sm={4} md={2}><StatTile label="School mean" value={`${data.schoolMean}%`} color={gradeColor(best?.grade)} /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatTile label="Stages" value={data.stages.length} /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatTile label="Learners assessed" value={data.scope.learnerCount} /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatTile label="At risk (all stages)" value={data.stages.reduce((s, st) => s + st.atRiskCount, 0)} color="#c62828" /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatTile label="Top stage" value={best ? best.stageCode : '—'} sub={best ? `${best.mean}%` : ''} color="#2e7d32" /></Grid>
        <Grid item xs={6} sm={4} md={2}><StatTile label="Needs support" value={weakest ? weakest.stageCode : '—'} sub={weakest ? `${weakest.mean}%` : ''} color="#c62828" /></Grid>
      </Grid>

      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Cohort mean by term
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>— each line is a stage, {data.academicYear}</Typography>
          </Typography>
          <MultiLineChart labels={data.terms} series={series} />
          <Box display="flex" gap={1.5} flexWrap="wrap" mt={1}>
            {data.stages.map((st, i) => (
              <Box key={st.stageCode} display="flex" alignItems="center" gap={0.5}>
                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: PALETTE[i % PALETTE.length] }} />
                <Typography variant="caption" color="text.secondary">{st.stageCode}</Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Stage snapshot</Typography>
          <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="stretch">
            {data.stages.map(st => (
              <Paper key={st.stageCode} variant="outlined" sx={{ p: 1.5, minWidth: 140, textAlign: 'center', borderColor: `${gradeColor(st.grade)}66`, bgcolor: `${gradeColor(st.grade)}0d` }}>
                <Typography variant="caption" color="text.secondary" display="block" noWrap>{st.stageCode} · {st.stageName}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 800, color: gradeColor(st.grade), lineHeight: 1.2 }}>{st.mean}%</Typography>
                <Chip size="small" label={st.grade || '—'} sx={{ bgcolor: gradeColor(st.grade), color: '#fff', fontWeight: 700, height: 20, mt: 0.5 }} />
                <Box mt={0.5}><TrendChip v={st.trend} /></Box>
                <Typography variant="caption" color="text.secondary" display="block">{st.assessedCount} learner(s){st.atRiskCount ? ` · ${st.atRiskCount} at risk` : ''}</Typography>
              </Paper>
            ))}
          </Box>
        </CardContent>
      </Card>

      <Card elevation={2} sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>Stage ranking</Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>Stage</strong></TableCell>
                  <TableCell align="center"><strong>Term trend</strong></TableCell>
                  <TableCell align="center"><strong>Mean</strong></TableCell>
                  <TableCell align="center"><strong>Grade</strong></TableCell>
                  <TableCell align="center"><strong>Latest</strong></TableCell>
                  <TableCell align="center"><strong>Δ</strong></TableCell>
                  <TableCell align="center"><strong>Learners</strong></TableCell>
                  <TableCell align="center"><strong>At risk</strong></TableCell>
                  <TableCell align="center"><strong>Projected</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ranked.map((st, i) => (
                  <TableRow key={st.stageCode} hover>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{st.stageCode} <Typography component="span" variant="caption" color="text.secondary">{st.stageName}</Typography></TableCell>
                    <TableCell align="center"><Sparkline values={st.points.map(p => p.value).filter(v => v != null)} color={gradeColor(st.grade)} /></TableCell>
                    <TableCell align="center"><strong style={{ color: gradeColor(st.grade) }}>{st.mean}%</strong></TableCell>
                    <TableCell align="center"><Chip size="small" label={st.grade || '—'} sx={{ bgcolor: gradeColor(st.grade), color: '#fff', fontWeight: 700, height: 20 }} /></TableCell>
                    <TableCell align="center">{st.latest != null ? `${st.latest}%` : '—'}</TableCell>
                    <TableCell align="center"><TrendChip v={st.trend} suffix="" /></TableCell>
                    <TableCell align="center">{st.assessedCount}</TableCell>
                    <TableCell align="center" sx={{ color: st.atRiskCount ? '#c62828' : 'text.secondary', fontWeight: st.atRiskCount ? 700 : 400 }}>{st.atRiskCount}</TableCell>
                    <TableCell align="center" sx={{ color: '#8e24aa' }}>{st.projection ? `${st.projection.projectedMean}%` : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Card elevation={2}>
        <CardContent>
          <Typography variant="subtitle2" fontWeight={700} gutterBottom>
            Stage × subject means
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>— colour = grade band; blank = subject not offered / no scores</Typography>
          </Typography>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 120 + data.subjects.length * 70 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'grey.100' }}>
                  <TableCell sx={{ position: 'sticky', left: 0, bgcolor: 'grey.100', zIndex: 1 }}><strong>Stage</strong></TableCell>
                  {data.subjects.map(s => <TableCell key={s.code} align="center"><strong>{s.name}</strong></TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.matrix.map(row => (
                  <TableRow key={row.stageCode} hover>
                    <TableCell sx={{ position: 'sticky', left: 0, bgcolor: '#fff', zIndex: 1, fontWeight: 600 }}>{row.stageCode}</TableCell>
                    {row.cells.map(c => (
                      <TableCell key={c.code} align="center" sx={c.mean != null ? { bgcolor: `${gradeColor(c.grade)}22`, color: gradeColor(c.grade), fontWeight: 700 } : { color: 'text.disabled' }}>
                        {c.mean != null ? `${c.mean}` : '—'}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </>
  );
}

// ── Excel export helpers ───────────────────────────────────────────────────────
const _save = (wb, name) => XLSX.writeFile(wb, `${name}-${new Date().toISOString().slice(0, 10)}.xlsx`);

export function exportLearnerXlsx(data) {
  const wb = XLSX.utils.book_new();
  const summary = [
    ['LearnAlytics — Learner report'],
    ['Name', data.learner?.name || ''],
    ['Adm No', data.learner?.admNo || ''],
    ['Stage', data.learner?.stage || ''],
    ['Overall mean', data.overall?.mean], ['Latest', data.overall?.latest], ['Best', data.overall?.best], ['Lowest', data.overall?.worst],
    ['Trend (pts)', data.overall?.trend], ['Trend/term', data.overall?.slopePerTerm],
    ['Projected next', data.projection ? `${data.projection.projectedMean}% (${data.projection.nextLabel})` : '—'],
    [], ['Risks'], ...data.risks.map(r => [r.level.toUpperCase(), r.text]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');
  if (data.byGrade?.length) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
      data.byGrade.map(g => ({ Stage: g.stageCode, Years: g.years, 'Terms': g.termsCount, 'Overall Mean %': g.mean, Grade: g.grade, 'Trend vs prev grade': g.trend }))), 'By Grade');
  }
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
    data.periods.map(p => ({ Period: p.label, Stage: p.stageCode, 'Overall Mean %': p.overallMean, Subjects: p.subjectCount, Grade: p.grade }))), 'Periods');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
    data.subjects.map(s => ({ Subject: s.name, 'Mean %': s.mean, 'Latest %': s.latest, 'Best %': s.best, 'Low %': s.worst, 'Trend': s.trend }))), 'Subjects');
  _save(wb, `LearnAlytics-${(data.learner?.name || 'learner').replace(/\s+/g, '_')}`);
}

function exportClassXlsx(data) {
  const wb = XLSX.utils.book_new();
  const summary = [
    ['LearnAlytics — Class report'],
    ['Scope', data.scope?.label || ''],
    ['Learners', data.scope?.learnerCount],
    ['Class mean', data.classMean],
    ['At risk', data.atRiskCount],
    ['Projected next', data.projection ? `${data.projection.projectedMean}% (${data.projection.nextLabel})` : '—'],
    [], ['Grade distribution'], ...Object.entries(data.distribution || {}).map(([b, n]) => [b, n]),
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
    data.learners.map(l => ({ Rank: l.rank, 'Adm No': l.admNo, Name: l.name, 'Mean %': l.mean, 'Latest %': l.latest, 'Trend': l.trend, Grade: l.grade }))), 'Learner Ranking');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
    data.subjects.map(s => ({ Subject: s.name, 'Mean %': s.mean, 'Latest %': s.latest, 'Trend': s.trend, 'Below 40': `${s.belowCount}/${s.entries}`, Grade: s.grade }))), 'Learning Areas');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
    data.periods.map(p => ({ Period: p.label, 'Class Mean %': p.classMean, Grade: p.grade }))), 'Class Trend');
  _save(wb, `LearnAlytics-${(data.scope?.label || 'class').replace(/\s+/g, '_')}`);
}

function exportStagesXlsx(data) {
  const wb = XLSX.utils.book_new();
  const summary = [
    ['LearnAlytics — Stage comparison'],
    ['Academic year', data.academicYear || ''],
    ['Stages', data.stages?.length],
    ['Learners assessed', data.scope?.learnerCount],
    ['School mean', data.schoolMean],
  ];
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
    data.stages.map(st => ({
      Stage: st.stageCode, Name: st.stageName, Learners: st.assessedCount,
      'Mean %': st.mean, Grade: st.grade, 'Latest %': st.latest, 'Trend': st.trend,
      'At Risk': st.atRiskCount, 'Projected next %': st.projection ? st.projection.projectedMean : '',
    }))), 'Stages');
  // Stage × term matrix
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
    data.stages.map(st => {
      const row = { Stage: st.stageCode };
      st.points.forEach(p => { row[p.label] = p.value; });
      return row;
    })), 'Term Means');
  // Stage × subject matrix
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(
    data.matrix.map(r => {
      const row = { Stage: r.stageCode };
      r.cells.forEach((c, i) => { row[data.subjects[i]?.name || c.code] = c.mean; });
      return row;
    })), 'Stage x Subject');
  _save(wb, `LearnAlytics-Stages-${data.academicYear || 'all'}`);
}

// ── Main (staff) LernAlytics with Learner / Class mode ─────────────────────────
export default function LernAlytics() {
  const navigate = useNavigate();
  const location = useLocation();
  const [mode, setMode] = useState(location.state?.mode === 'class' ? 'class' : 'learner');

  // shared filters
  const [subject, setSubject] = useState('');
  const [term, setTerm] = useState('');
  const [fromYear, setFromYear] = useState('');
  const [toYear, setToYear] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // learner mode
  const [learners, setLearners] = useState([]);
  const [sel, setSel] = useState(null);
  // class mode
  const [stages, setStages] = useState([]);
  const [classes, setClasses] = useState([]);
  const [stage, setStage] = useState(location.state?.stageCode || '');
  const [classCode, setClassCode] = useState(location.state?.classCode || '');
  const [year, setYear] = useState('');

  useEffect(() => {
    axios.get(`${API}/api/academics/learners`, { headers: hdrs() }).then(r => {
      const list = r.data || []; setLearners(list);
      const admNo = location.state?.learnerAdmNo;
      if (admNo) { const m = list.find(l => l.admNo === admNo); if (m) setSel(m); }
    }).catch(() => {});
    axios.get(`${API}/api/stages`, { headers: hdrs() }).then(r => setStages(r.data || [])).catch(() => {});
  }, []); // eslint-disable-line

  // Load classes when a stage is picked (class mode)
  useEffect(() => {
    if (!stage) { setClasses([]); return; }
    axios.get(`${API}/api/academics/classes?stageCode=${stage}`, { headers: hdrs() })
      .then(r => setClasses(r.data || [])).catch(() => setClasses([]));
  }, [stage]);

  const run = useCallback(async () => {
    setError('');
    if (mode === 'learner') {
      if (!sel) { setData(null); return; }
      setLoading(true);
      try {
        const params = { admNo: sel.admNo };
        if (subject) params.learningAreaCode = subject;
        if (term) params.term = term;
        if (fromYear) params.fromYear = fromYear;
        if (toYear) params.toYear = toYear;
        const { data } = await axios.get(`${API}/api/academics/lernalytics`, { headers: hdrs(), params });
        setData(data);
      } catch (err) { setError(err.response?.data?.error || 'Failed to load analytics'); }
      finally { setLoading(false); }
    } else if (mode === 'stages') {
      setLoading(true);
      try {
        const params = {};
        if (year) params.academicYear = year;
        if (subject) params.learningAreaCode = subject;
        const { data } = await axios.get(`${API}/api/academics/lernalytics/stages`, { headers: hdrs(), params });
        setData(data);
      } catch (err) { setError(err.response?.data?.error || 'Failed to load stage comparison'); }
      finally { setLoading(false); }
    } else {
      if (!classCode && !stage) { setData(null); return; }
      setLoading(true);
      try {
        const params = {};
        if (classCode) params.classCode = classCode; else params.stageCode = stage;
        if (subject) params.learningAreaCode = subject;
        if (term) params.term = term;
        if (year) params.academicYear = year;
        const { data } = await axios.get(`${API}/api/academics/lernalytics/class`, { headers: hdrs(), params });
        setData(data);
      } catch (err) { setError(err.response?.data?.error || 'Failed to load class analytics'); }
      finally { setLoading(false); }
    }
  }, [mode, sel, subject, term, fromYear, toYear, classCode, stage, year]);

  useEffect(() => { run(); }, [run]);

  const years = data?.filters?.years || [];
  const subjectsList = data?.filters?.subjects || [];

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 3, mb: 4, px: { xs: 1, sm: 2 } }}>
      <style>{`@media print {
        .MuiDrawer-root, .MuiAppBar-root, .lern-noprint { display: none !important; }
        body { background: #fff !important; }
        .MuiCard-root { box-shadow: none !important; border: 1px solid #e0e0e0; }
      }`}</style>
      <Card elevation={3} sx={{ mb: 2 }} className="lern-noprint">
        <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={1}>
            <Box display="flex" alignItems="center" gap={1}>
              <Analytics sx={{ color: BRAND, fontSize: { xs: 24, sm: 30 } }} />
              <Box>
                <Typography variant="h5" sx={{ color: BRAND, fontWeight: 800, fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>LearnAlytics</Typography>
                <Typography variant="caption" color="text.secondary">Performance over time — trends, projections & risks</Typography>
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title="Export to Excel"><span><IconButton disabled={!data} onClick={() => mode === 'class' ? exportClassXlsx(data) : mode === 'stages' ? exportStagesXlsx(data) : exportLearnerXlsx(data)} sx={{ bgcolor: '#e8f5e9' }}><Download sx={{ color: '#2e7d32' }} /></IconButton></span></Tooltip>
              <Tooltip title="Print / Save as PDF"><span><IconButton disabled={!data} onClick={() => window.print()} sx={{ bgcolor: '#eceff1' }}><Print sx={{ color: '#455a64' }} /></IconButton></span></Tooltip>
              <Tooltip title="Pathway Insight"><IconButton onClick={() => navigate('/academics/pathway-insight')} sx={{ bgcolor: '#e0f2f1' }}><Insights sx={{ color: BRAND }} /></IconButton></Tooltip>
              <Tooltip title="Assessment Reports"><IconButton onClick={() => navigate('/academics/assessment-report')} sx={{ bgcolor: '#e0f2f1' }}><Assessment sx={{ color: '#004d40' }} /></IconButton></Tooltip>
              <Tooltip title="Academics"><IconButton onClick={() => navigate('/academics')}><Home /></IconButton></Tooltip>
            </Box>
          </Box>

          <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => { if (v) { setMode(v); setData(null); } }} sx={{ mb: 2 }}>
            <ToggleButton value="learner"><Person fontSize="small" sx={{ mr: 0.5 }} /> Learner</ToggleButton>
            <ToggleButton value="class"><Groups fontSize="small" sx={{ mr: 0.5 }} /> Class</ToggleButton>
            <ToggleButton value="stages"><Leaderboard fontSize="small" sx={{ mr: 0.5 }} /> Stages</ToggleButton>
          </ToggleButtonGroup>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
            {mode === 'learner' && (
              <Autocomplete options={learners} value={sel} getOptionLabel={o => `${o.admNo} — ${o.name}`}
                isOptionEqualToValue={(o, v) => o.admNo === v.admNo} onChange={(_, v) => setSel(v)}
                sx={{ minWidth: 260, flexGrow: { xs: 1, sm: 0 } }}
                renderInput={p => <TextField {...p} label="Search Learner" size="small" placeholder="Type to filter…" />} />
            )}
            {mode === 'class' && (
              <>
                <TextField select size="small" label="Stage" sx={{ minWidth: 130 }} value={stage} onChange={e => { setStage(e.target.value); setClassCode(''); }}>
                  <MenuItem value=""><em>— Select —</em></MenuItem>
                  {stages.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Class" sx={{ minWidth: 150 }} value={classCode} onChange={e => setClassCode(e.target.value)}>
                  <MenuItem value=""><em>Whole stage</em></MenuItem>
                  {classes.map(c => <MenuItem key={c.classCode} value={c.classCode}>{c.className}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="Year" sx={{ width: 120 }} value={year} onChange={e => setYear(e.target.value)}>
                  <MenuItem value=""><em>All</em></MenuItem>
                  {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
              </>
            )}
            {mode === 'stages' && (
              <TextField select size="small" label="Year" sx={{ width: 130 }} value={year} onChange={e => setYear(e.target.value)}>
                <MenuItem value=""><em>Latest</em></MenuItem>
                {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
              </TextField>
            )}
            <TextField select size="small" label="Subject" sx={{ minWidth: 150 }} value={subject} onChange={e => setSubject(e.target.value)}>
              <MenuItem value=""><em>All subjects</em></MenuItem>
              {subjectsList.map(s => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
            </TextField>
            {mode !== 'stages' && (
              <TextField select size="small" label="Term" sx={{ width: 110 }} value={term} onChange={e => setTerm(e.target.value)}>
                <MenuItem value=""><em>All</em></MenuItem>
                {[1, 2, 3].map(t => <MenuItem key={t} value={String(t)}>Term {t}</MenuItem>)}
              </TextField>
            )}
            {mode === 'learner' && (
              <>
                <TextField select size="small" label="From Yr" sx={{ width: 110 }} value={fromYear} onChange={e => setFromYear(e.target.value)}>
                  <MenuItem value=""><em>Earliest</em></MenuItem>
                  {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
                <TextField select size="small" label="To Yr" sx={{ width: 110 }} value={toYear} onChange={e => setToYear(e.target.value)}>
                  <MenuItem value=""><em>Latest</em></MenuItem>
                  {years.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                </TextField>
              </>
            )}
            {loading && <CircularProgress size={20} />}
          </Box>
        </CardContent>
      </Card>

      {mode === 'learner' && !sel && <Alert severity="info">Select a learner to view their performance analytics.</Alert>}
      {mode === 'class' && !classCode && !stage && <Alert severity="info">Select a stage (and optionally a class) to view cohort analytics.</Alert>}

      {data && mode === 'learner' && sel && (
        <>
          <Box mb={2} px={0.5}>
            <Typography variant="h6" fontWeight={800}>{data.learner?.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Adm {data.learner?.admNo}{data.learner?.stage ? ` · ${data.learner.stage}` : ''}{data.learner?.classCode ? ` · ${data.learner.classCode}` : ''} · {data.periods.length} period(s)
            </Typography>
          </Box>
          <LearnerAnalyticsBody data={data} />
        </>
      )}

      {data && mode === 'class' && (
        <>
          <Box mb={2} px={0.5}>
            <Typography variant="h6" fontWeight={800}>{data.scope?.label}</Typography>
            <Typography variant="body2" color="text.secondary">{data.scope?.learnerCount} learner(s) · {data.periods.length} period(s)</Typography>
          </Box>
          <ClassAnalyticsBody data={data} />
        </>
      )}

      {data && mode === 'stages' && (
        <>
          <Box mb={2} px={0.5}>
            <Typography variant="h6" fontWeight={800}>Stage comparison · {data.academicYear || 'all years'}</Typography>
            <Typography variant="body2" color="text.secondary">{data.stages?.length || 0} stage(s) · {data.scope?.learnerCount || 0} learner(s) assessed</Typography>
          </Box>
          <StageComparisonBody data={data} />
        </>
      )}
    </Box>
  );
}
